import { FC } from 'react';
import { css } from '@emotion/react';
import { colors, spacing, fontStyles } from '../../../styles';
import { ToolRendererProps } from './types';
import { DataCard } from './shared/DataCard';

// TypeScript interfaces for transcript search
interface TranscriptMatch {
  symbol?: string;
  quarter?: string;
  excerpt?: string;
  content_with_highlights?: string;
  highlighted?: string;
  content?: string;
  text?: string;
  speaker?: string;
  section?: string;
  position?: number;
  relevance_score?: number;
  relevance?: number;
}

interface TranscriptSearchOutput {
  transcript_search_results?: {
    query?: string;
    transcript_info?: { symbol?: string };
    matches?: TranscriptMatch[];
  };
  search_query?: string;
  query?: string;
  results?: TranscriptMatch[];
  data?: TranscriptMatch[];
  transcripts?: TranscriptMatch[];
}

// Constants for transcript search display
const MAX_TRANSCRIPT_RESULTS_DISPLAY = 3;

// Helper functions for transcript search
const getSearchQuery = (output: TranscriptSearchOutput): string | undefined => {
  return output.transcript_search_results?.query || output.search_query || output.query;
};

const getTranscriptResults = (output: TranscriptSearchOutput): TranscriptMatch[] | null => {
  return output.transcript_search_results?.matches || output.results || output.data || output.transcripts || null;
};

const getRelevanceScore = (result: TranscriptMatch): number | null => {
  // Normalize relevance score from different field names
  if (result.relevance_score !== undefined) return result.relevance_score;
  if (typeof result.relevance === 'number') return result.relevance;
  return null;
};

export const TranscriptSearchRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return (
      <div
        css={css`
          ${fontStyles.mouse}
          color: ${colors.silver};
        `}
      >
        Searching transcripts...
      </div>
    );
  }

  // Extract search query and results using helper functions
  const searchQuery = getSearchQuery(output);
  const results = getTranscriptResults(output);

  // Get symbol/quarter from parent-level transcript_info
  const transcriptInfo = (output as TranscriptSearchOutput).transcript_search_results?.transcript_info;

  if (!results || (Array.isArray(results) && results.length === 0)) {
    return (
      <div
        css={css`
          ${fontStyles.mouse}
          color: ${colors.silver};
        `}
      >
        {searchQuery ? (
          <>
            No transcript results found for <strong>&quot;{searchQuery}&quot;</strong>
          </>
        ) : (
          'No transcript results found'
        )}
      </div>
    );
  }

  const transcriptResults = Array.isArray(results) ? results : [results];

  return (
    <div
      css={css`
        margin: ${spacing.centi} 0 0 0;
      `}
    >
      {/* Parent-level transcript info */}
      {transcriptInfo?.symbol && (
        <div
          css={css`
            font-weight: 500;
            color: ${colors.petrolBlue};
            margin-bottom: ${spacing.centi};

            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
          `}
        >
          {transcriptInfo.symbol} {(transcriptInfo as any).quarter && `- ${(transcriptInfo as any).quarter}`}
        </div>
      )}
      <div
        css={css`
          ${fontStyles.flea}
          color: ${colors.silver};
          margin-bottom: ${spacing.centi};
        `}
      >
        Found{' '}
        <span
          css={css`
            font-family: monospace;
            font-variant-numeric: tabular-nums;
          `}
        >
          {transcriptResults.length}
        </span>{' '}
        transcript result{transcriptResults.length === 1 ? '' : 's'}
      </div>
      {transcriptResults.slice(0, MAX_TRANSCRIPT_RESULTS_DISPLAY).map((result: any, index: number) => (
        <div
          key={index}
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <DataCard accentColor={colors.petrolBlue} padding={spacing.centi}>
            {(result.symbol || result.quarter) && !transcriptInfo?.symbol && (
              <div
                css={css`
                  font-weight: 500;
                  color: ${colors.petrolBlue};
                  margin-bottom: ${spacing.milli};

                  html[data-theme='dark'] & {
                    color: ${colors.skyBlue};
                  }
                `}
              >
                {result.symbol} {result.quarter && `- ${result.quarter}`}
              </div>
            )}
            {result.speaker && (
              <div
                css={css`
                  ${fontStyles.mouse}
                  font-weight: 600;
                  margin-bottom: ${spacing.micro};
                  color: ${colors.tungsten};

                  html[data-theme='dark'] & {
                    color: ${colors.cloud};
                  }
                `}
              >
                {result.speaker}
              </div>
            )}
            <div
              css={css`
                ${fontStyles.mouse}
                color: ${colors.liquorice};

                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {result.highlighted ||
                result.excerpt ||
                result.content_with_highlights ||
                result.content ||
                result.text ||
                'Transcript excerpt available'}
            </div>
            {getRelevanceScore(result) !== null && (
              <div
                css={css`
                  ${fontStyles.flea}
                  color: ${colors.silver};
                  margin-top: ${spacing.milli};
                `}
              >
                Relevance:{' '}
                <span
                  css={css`
                    font-family: monospace;
                    font-variant-numeric: tabular-nums;
                  `}
                >
                  {(getRelevanceScore(result)! * 100).toFixed(0)}%
                </span>
              </div>
            )}
          </DataCard>
        </div>
      ))}
    </div>
  );
};
